import { Dispatch } from 'redux';
import { getAllProductCategories } from 'app/shared/api/getProductCategories';
import { getProductCategoryById as getProductCategoryByIdApi } from 'app/shared/api/getProductCategoryById';
import {
  saveProductCategory as saveProductCategoryApi,
  SaveProductCategoryParams,
} from 'app/shared/api/saveProductCategory';

// ─── Types ─────────────────────────────────────────────────────────────────────
export type ProductCategory = {
  id: number;
  name: string;
  createdBy: string;
  lastModifiedBy: string;
  active: boolean;
};

export type SavePayload = SaveProductCategoryParams;

// ─── Action Types ──────────────────────────────────────────────────────────────
export const FETCH_PRODUCTS = 'PRODUCT_MANAGER/FETCH_PRODUCTS';
export const SET_PRODUCTS = 'PRODUCT_MANAGER/SET_PRODUCTS';

export const SAVE_PRODUCT_REQUEST = 'PRODUCT_MANAGER/SAVE_PRODUCT_REQUEST';
export const SAVE_PRODUCT_SUCCESS = 'PRODUCT_MANAGER/SAVE_PRODUCT_SUCCESS';
export const SAVE_PRODUCT_FAILURE = 'PRODUCT_MANAGER/SAVE_PRODUCT_FAILURE';

export const PRODUCT_SET_FILTER = 'PRODUCT_MANAGER/SET_FILTER';
export const PRODUCT_MODAL_OPEN = 'PRODUCT_MANAGER/MODAL_OPEN';
export const PRODUCT_MODAL_CLOSE = 'PRODUCT_MANAGER/MODAL_CLOSE';

// ─── Action Creators ───────────────────────────────────────────────────────────
export const setProducts = (items: ProductCategory[]) => ({
  type: SET_PRODUCTS,
  payload: items,
});

export const setFilter = (value: string) => ({
  type: PRODUCT_SET_FILTER,
  payload: value,
});

export const openModal = (editing: ProductCategory | null) => ({
  type: PRODUCT_MODAL_OPEN,
  payload: editing,
});

export const closeModal = () => ({
  type: PRODUCT_MODAL_CLOSE,
});

// ─── Thunks ────────────────────────────────────────────────────────────────────
export const fetchProductCategories = () => async (dispatch: Dispatch) => {
  dispatch({ type: FETCH_PRODUCTS });
  try {
    const data = await getAllProductCategories();
    dispatch(setProducts(Array.isArray(data) ? (data as ProductCategory[]) : []));
  } catch {
    dispatch(setProducts([]));
  }
};

export const fetchProductCategoryById =
  (id: number) =>
  async (): Promise<ProductCategory | null> => {
    try {
      const dto = await getProductCategoryByIdApi(id);
      return (dto as ProductCategory) ?? null;
    } catch {
      return null;
    }
  };

export const saveProductCategory =
  (payload: SavePayload) =>
  async (dispatch: Dispatch) => {
    dispatch({ type: SAVE_PRODUCT_REQUEST });
    try {
      const res = await saveProductCategoryApi(payload);
      if (!res.ok) {
        dispatch({ type: SAVE_PRODUCT_FAILURE, error: res.message });
        return { ok: false, message: res.message };
        }
      dispatch({ type: SAVE_PRODUCT_SUCCESS });
      await (fetchProductCategories() as any)(dispatch);
      return { ok: true };
    } catch (error: any) {
      const message = error?.message || 'Failed to save product category.';
      dispatch({ type: SAVE_PRODUCT_FAILURE, error: message });
      return { ok: false, message };
    }
  };
